/**
 * A edição no documento da mesa (E202.F2, GUS-145, ADR-0145 §5–§6): o trecho aberto, os rascunhos da
 * pessoa, a gravação e as ações. Os trechos vêm do próprio documento (a mesa lê a saída uma vez só);
 * aqui se leem só os rascunhos.
 *
 * O servidor das edições é o de sempre (ADR-0122/0136): o rascunho por pausa, "Salvar" com a guarda
 * otimista e o `versaoFoiSalva` da falha de rede (E200.F3). Depois de salvar, a mesa relê o documento
 * e o pacote (a prévia em PDF está sendo refeita). A recusa por outra edição, outra análise ou trecho
 * mudado mantém o bloco aberto com o texto e relê o documento, para a pessoa ver o que mudou.
 *
 * Correções do round 1 (E202.F2.C02–C04, F-E202.F2-02/03/04):
 * - o editor só abre com os rascunhos lidos (`carga`): o rascunho guardado é o começo dele, e a falha
 *   da leitura tem "Tentar de novo" em vez de um editor que guardaria o texto do documento por cima;
 * - cada ação pertence à sessão do trecho que a pediu (`geracao`): enquanto salva, descarta ou
 *   reaplica, abrir e fechar travam, e a resposta só fecha ou marca o bloco se a sessão é a mesma;
 * - "Guardar e sair" e "Descartar e sair" esperam a confirmação (`guardarJa`, `descartarJa`).
 *
 * E205.F2.C03 (F-E205.F2-01): a resposta de uma guarda anterior do mesmo trecho não repõe nos rascunhos
 * da mesa a que foi pedida depois dela.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CorrecaoDoRascunhoError,
  reaplicarEdicao,
  type AcaoDaEdicao,
  type MotivoDaFalha,
  type SaidaDaEdicao,
  type TrechoNaTelaData,
} from '@/lib/correcaoDoRascunho';
import type { DocumentoDaSaidaData } from '@/lib/documentoDaSaida';
import {
  descartarRascunho,
  edicaoDaVersao,
  guardarRascunho,
  lerRascunhos,
  lerTrechosDaSaida,
  salvarVersao,
  type ConteudoDoEditor,
  type RascunhoNaTelaData,
  type SituacaoDoRascunho,
  versaoFoiSalva,
} from '@/lib/editorManual';
import type { PacoteDoCasoData } from '@/lib/preparacao_do_pacote';

/** A recusa que muda o que a pessoa vê: o documento é relido, e o texto dela fica no bloco. */
const RELEEM: ReadonlySet<MotivoDaFalha> = new Set([
  'outra_edicao',
  'outra_rodada',
  'trecho_mudou',
]);

/** E209.F2.T02 (GUS-296 item 5d): o arquivo da prévia que a mesa mostra da saída (`null`: nenhum);
 * a saída sendo refeita não conta, como em `PreviaDaSaida`. */
function previaDa(pacote: PacoteDoCasoData | null, saida: SaidaDaEdicao): string | null {
  const linha = pacote?.linhas.find((l) => l.chave === saida);
  if (linha === undefined || linha.estado === 'em_andamento' || linha.estado === 'aguardando') {
    return null;
  }
  return linha.artefato_id ?? null;
}

function motivoDe(err: unknown): MotivoDaFalha {
  return err instanceof CorrecaoDoRascunhoError ? err.motivo : 'indisponivel';
}

export type ResultadoDaGravacao = 'salva' | 'nao_salva' | null;

/** A leitura dos rascunhos da pessoa na saída: o editor só abre com ela resolvida. */
export type CargaDosRascunhos = 'lendo' | 'lidos' | 'falhou';

export interface EdicaoNoDocumento {
  rascunhos: ReadonlyMap<string, RascunhoNaTelaData>;
  aberto: string | null;
  /** A ação com que o bloco abriu ("Restaurar" no trecho tirado); `null`: a de sempre. */
  pedida: AcaoDaEdicao | null;
  salvando: boolean;
  falha: MotivoDaFalha | null;
  /** O que a última gravação deu: a versão salva ou não salva (`null`: nada a dizer). */
  gravacao: ResultadoDaGravacao;
  /** Um bloco aberto com algo que ainda não é versão salva (para sair sem perder, T08). */
  sujo: boolean;
  marcarSujo: (sujo: boolean) => void;
  abrir: (trechoId: string, acao?: AcaoDaEdicao) => void;
  fechar: () => void;
  guardar: (trecho: TrechoNaTelaData, conteudo: ConteudoDoEditor) => Promise<SituacaoDoRascunho>;
  salvar: (trecho: TrechoNaTelaData, conteudo: ConteudoDoEditor) => void;
  descartar: (trecho: TrechoNaTelaData) => void;
  reaplicar: (edicaoId: string) => void;
  carga: CargaDosRascunhos;
  tentarRascunhos: () => void;
  /** O bloco aberto registra como guardar já o que ainda não foi ao servidor (`null`: nenhum). */
  registrarGuarda: (guarda: (() => Promise<void>) | null) => void;
  /** Guarda já o rascunho do bloco aberto; a recusa chega a quem chamou. */
  guardarJa: () => Promise<void>;
  /** Descarta o rascunho e espera a confirmação; a recusa chega a quem chamou. */
  descartarJa: (trecho: TrechoNaTelaData) => Promise<void>;
}

/** Os rascunhos da pessoa na saída, relidos depois de cada ação; a resposta antiga não entra. */
function useRascunhos(client: AxiosInstance, caseId: string, saida: SaidaDaEdicao) {
  const [rascunhos, setRascunhos] = useState<RascunhoNaTelaData[]>([]);
  const [carga, setCarga] = useState<CargaDosRascunhos>('lendo');
  const ultima = useRef(0);
  const reler = useCallback(async () => {
    const ordem = ++ultima.current;
    try {
      const lidos = await lerRascunhos(client, caseId, saida);
      if (ordem !== ultima.current) return;
      setRascunhos(lidos.rascunhos);
      setCarga('lidos');
    } catch {
      // A falha de uma releitura não apaga o que já se leu; a da primeira leitura trava o editor.
      if (ordem === ultima.current) setCarga((c) => (c === 'lidos' ? c : 'falhou'));
    }
  }, [client, caseId, saida]);
  useEffect(() => {
    setRascunhos([]);
    setCarga('lendo');
    void reler();
  }, [reler]);
  const tentarDeNovo = useCallback(() => {
    setCarga('lendo');
    void reler();
  }, [reler]);
  return { rascunhos, setRascunhos, reler, carga, tentarDeNovo };
}

export function useEdicaoNoDocumento(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
  documento: DocumentoDaSaidaData | null,
  aoMudar: { versaoSalva: () => void; reler: () => void },
  /** E209.F2.T02: a leitura do pacote; a prévia nova depois da gravação encerra o "Versão salva". */
  pacote: PacoteDoCasoData | null = null,
): EdicaoNoDocumento {
  const lidos = useRascunhos(client, caseId, saida);
  const { rascunhos, setRascunhos, reler: relerRascunhos } = lidos;
  const [aberto, setAberto] = useState<string | null>(null);
  const [pedida, setPedida] = useState<AcaoDaEdicao | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<MotivoDaFalha | null>(null);
  const [gravacao, setGravacao] = useState<ResultadoDaGravacao>(null);
  const [sujo, setSujo] = useState(false);
  // E209.F2.T02: "a prévia em PDF está sendo refeita" vale até a leitura do pacote trazer outra prévia
  // pronta da saída que a vista na gravação (a que a gravação desatualizou não conta).
  const previa = previaDa(pacote, saida);
  const previaAtual = useRef(previa);
  previaAtual.current = previa;
  const previaNaGravacao = useRef<string | null>(null);
  useEffect(() => {
    if (gravacao !== 'salva' || previa === null || previa === previaNaGravacao.current) return;
    setGravacao(null);
  }, [gravacao, previa]);
  // O trecho descartado não guarda mais rascunho (o bloco que fecha guarda o que estava pendente).
  const descartados = useRef(new Set<string>());
  const avaliacaoId = documento?.avaliacao_id ?? null;
  // A sessão do bloco: abrir, fechar e trocar de saída mudam a geração; a ação em curso trava as duas.
  const geracao = useRef(0);
  const ocupado = useRef(false);
  const guarda = useRef<(() => Promise<void>) | null>(null);
  // A vez de cada guarda por trecho: só a última pedida escreve nos rascunhos.
  const guardas = useRef(new Map<string, number>());

  // Outra saída fecha o bloco; a montagem, não (o trecho apontado abre antes deste efeito).
  const saidaVista = useRef(saida);
  useEffect(() => {
    if (saidaVista.current === saida) return;
    saidaVista.current = saida;
    geracao.current += 1;
    setAberto(null);
    setSujo(false);
  }, [saida]);

  const abrir = useCallback((trechoId: string, acao?: AcaoDaEdicao) => {
    if (ocupado.current) return;
    geracao.current += 1;
    setFalha(null);
    setGravacao(null);
    setPedida(acao ?? null);
    descartados.current.delete(trechoId);
    setAberto(trechoId);
  }, []);
  const fechar = useCallback(() => {
    if (ocupado.current) return;
    geracao.current += 1;
    setAberto(null);
    setSujo(false);
  }, []);
  const registrarGuarda = useCallback((g: (() => Promise<void>) | null) => {
    guarda.current = g;
  }, []);

  /** Fecha o bloco só se ele ainda é o da sessão que pediu a ação. */
  function encerrar(minha: number): void {
    if (geracao.current !== minha) return;
    geracao.current += 1;
    setFalha(null);
    setAberto(null);
    setSujo(false);
  }

  /** A ação foi salva: fecha o bloco dela e a mesa relê o documento e o pacote. */
  function salva(minha: number): void {
    encerrar(minha);
    previaNaGravacao.current = previaAtual.current;
    setGravacao('salva');
    aoMudar.versaoSalva();
    void relerRascunhos();
  }

  /** A falha diz respeito ao bloco da sessão que pediu; outro bloco não a herda. */
  function falhou(minha: number, motivo: MotivoDaFalha): void {
    if (geracao.current !== minha) return;
    setFalha(motivo);
  }

  /** Uma ação por vez: enquanto ela corre, abrir e fechar travam. */
  async function agir(acao: (minha: number) => Promise<void>): Promise<void> {
    const minha = geracao.current;
    ocupado.current = true;
    setSalvando(true);
    try {
      await acao(minha);
    } finally {
      ocupado.current = false;
      setSalvando(false);
    }
  }

  async function chegou(confirmada: Parameters<typeof versaoFoiSalva>[1]): Promise<boolean> {
    try {
      return versaoFoiSalva(await lerTrechosDaSaida(client, caseId, saida), confirmada);
    } catch {
      return false;
    }
  }

  async function guardar(trecho: TrechoNaTelaData, conteudo: ConteudoDoEditor) {
    if (avaliacaoId === null) throw new CorrecaoDoRascunhoError('sem_resultado');
    if (descartados.current.has(trecho.id)) return 'vigente';
    const vez = (guardas.current.get(trecho.id) ?? 0) + 1;
    guardas.current.set(trecho.id, vez);
    const guardado = await guardarRascunho(client, caseId, saida, trecho.id, {
      ...conteudo,
      avaliacao_id: avaliacaoId,
      anterior_id: trecho.vigente?.id ?? null,
    });
    if (guardas.current.get(trecho.id) === vez) {
      setRascunhos((r) => [guardado, ...r.filter((x) => x.trecho_id !== trecho.id)]);
    }
    return guardado.situacao;
  }

  const salvarAgora = (trecho: TrechoNaTelaData, conteudo: ConteudoDoEditor) =>
    agir(async (minha) => {
      if (avaliacaoId === null) return;
      const edicao = edicaoDaVersao(trecho, conteudo);
      setFalha(null);
      try {
        await salvarVersao(client, caseId, { saida, avaliacao_id: avaliacaoId, edicoes: [edicao] });
        salva(minha);
      } catch (err) {
        const motivo = motivoDe(err);
        if (motivo === 'indisponivel' && (await chegou(edicao))) {
          salva(minha);
          return;
        }
        falhou(minha, motivo);
        setGravacao('nao_salva');
        if (RELEEM.has(motivo)) aoMudar.reler();
      }
    });

  /** `lancar`: quem chama espera e recebe a recusa ("Descartar e sair"). */
  const descartarAgora = (trecho: TrechoNaTelaData, lancar: boolean) =>
    agir(async (minha) => {
      descartados.current.add(trecho.id);
      try {
        await descartarRascunho(client, caseId, saida, trecho.id);
      } catch (err) {
        descartados.current.delete(trecho.id);
        falhou(minha, motivoDe(err));
        if (lancar) throw err;
        return;
      }
      setRascunhos((r) => r.filter((x) => x.trecho_id !== trecho.id));
      setGravacao(null);
      encerrar(minha);
    });

  const reaplicarAgora = (edicaoId: string) =>
    agir(async (minha) => {
      try {
        await reaplicarEdicao(client, caseId, edicaoId);
        salva(minha);
      } catch (err) {
        falhou(minha, motivoDe(err));
      }
    });

  const porTrecho = useMemo(() => new Map(rascunhos.map((r) => [r.trecho_id, r])), [rascunhos]);
  return {
    rascunhos: porTrecho,
    aberto,
    pedida,
    salvando,
    falha,
    gravacao,
    sujo,
    marcarSujo: setSujo,
    abrir,
    fechar,
    guardar,
    salvar: (trecho, conteudo) => void salvarAgora(trecho, conteudo),
    descartar: (trecho) => void descartarAgora(trecho, false),
    reaplicar: (edicaoId) => void reaplicarAgora(edicaoId),
    carga: lidos.carga,
    tentarRascunhos: lidos.tentarDeNovo,
    registrarGuarda,
    guardarJa: async () => {
      await guarda.current?.();
    },
    descartarJa: (trecho) => descartarAgora(trecho, true),
  };
}
